import { zodResolver } from '@hookform/resolvers/zod'
import { Building2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

const tenantSchema = z.object({
  tenant_name: z
    .string()
    .min(2, 'Workspace name must be at least 2 characters')
    .max(100, 'Workspace name must be less than 100 characters')
    .trim(),
})

type TenantFormData = z.infer<typeof tenantSchema>

interface TenantSetupDialogProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: (tenantName: string) => void
  userEmail?: string
}

// Helper function to generate workspace name based on email
const generateWorkspaceNameFromEmail = (email?: string): string => {
  if (!email) return 'My Workspace'

  const username = email.split('@')[0]

  // Handle email formats like 'firstname.lastname@example.com'
  if (username.includes('.')) {
    return (
      username
        .split('.')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ') + "'s Workspace"
    )
  }

  // Handle simple usernames like 'test@gmail.com' or 'john@example.com'
  return username.charAt(0).toUpperCase() + username.slice(1) + "'s Workspace"
}

export function TenantSetupDialog({
  isOpen,

  onSuccess,
  userEmail,
}: TenantSetupDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm<TenantFormData>({
    resolver: zodResolver(tenantSchema),
    mode: 'onChange',
  })

  const suggestedWorkspaceName = generateWorkspaceNameFromEmail(userEmail)

  const onSubmit = async (data: TenantFormData) => {
    setIsSubmitting(true)
    setError(null)

    try {
      // Call the onSuccess callback provided by TenantProvider
      // TenantProvider will handle the API call and state updates
      await onSuccess(data.tenant_name)

      reset()
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to update workspace name. Please try again.'
      )
      toast.error(extractErrorMessage(err))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={() => {}} modal>
      <DialogContent
        className='sm:max-w-2xl'
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        showCloseButton={false}
      >
        <DialogHeader className='text-center'>
          <div className='mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4'>
            <Building2 className='w-6 h-6 text-primary' />
          </div>
          <DialogTitle className='text-xl'>Set up your workspace</DialogTitle>
          <DialogDescription className='text-balance'>
            Welcome to Coobird! Let's get started by setting up your workspace.
            {userEmail && (
              <span className='block text-sm text-muted-foreground mt-2'>
                Signed in as {userEmail}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className='space-y-5 mt-5'>
          <div className='space-y-3'>
            <Label htmlFor='tenant_name'>
              Workspace Name <span className='text-red-500'>*</span>
            </Label>
            <Input
              id='tenant_name'
              placeholder={suggestedWorkspaceName}
              {...register('tenant_name')}
              disabled={isSubmitting}
              className={errors.tenant_name ? 'border-red-500' : ''}
            />
            {errors.tenant_name && (
              <p className='text-sm text-red-500'>
                {errors.tenant_name.message}
              </p>
            )}
            {error && <p className='text-sm text-red-500'>{error}</p>}
          </div>

          <DialogFooter className='flex gap-2 sm:gap-0 mt-20'>
            <Button
              type='submit'
              disabled={!isValid || isSubmitting}
              className='w-full sm:w-auto'
            >
              {isSubmitting ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Setting up...
                </>
              ) : (
                'Complete Setup'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
